import { Menu, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { readBuilder } from '../data/stackBuilder'
import { repository } from '../data/repository'
import type { Meta } from '../data/types'
import { formatCount } from '../formatCount'
import { useLanguage, type Language } from '../i18n'

const nav = [
  { to: '/app', key: 'newResearch' as const, end: true },
  { to: '/stacks', key: 'stacks' as const },
  { to: '/substances', key: 'library' as const },
  { to: '/interactions', key: 'pairs' as const },
  { to: '/evidence', key: 'evidence' as const },
  { to: '/builder', key: 'builder' as const },
]

function sectionIndex(pathname: string) {
  if (pathname.startsWith('/runs/')) return 0
  const index = nav.findIndex(({ to }) => pathname === to || pathname.startsWith(`${to}/`))
  return index < 0 ? 0 : index
}

function mobileLabel(key: (typeof nav)[number]['key'], language: Language) {
  const labels = {
    ru: { newResearch: 'Подбор', stacks: 'Стеки', library: 'Библиотека', pairs: 'Проверка', evidence: 'Источники', builder: 'Мой стек' },
    en: { newResearch: 'Build', stacks: 'Stacks', library: 'Library', pairs: 'Check', evidence: 'Sources', builder: 'My stack' },
  }
  return labels[language][key]
}

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [meta, setMeta] = useState<Meta | null>(null)
  const [builderCount, setBuilderCount] = useState(() => readBuilder().length)
  const location = useLocation()
  const { tr, language } = useLanguage()
  const activeSectionIndex = sectionIndex(location.pathname)
  const activeSection = nav[activeSectionIndex]

  useEffect(() => {
    repository.getMeta().then(setMeta).catch(() => setMeta(null))
    const updateBuilder = () => setBuilderCount(readBuilder().length)
    window.addEventListener('biograph-stack-change', updateBuilder)
    return () => window.removeEventListener('biograph-stack-change', updateBuilder)
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
    setMobileOpen(false)
  }, [location.pathname])

  return (
    <div className={`app-shell ${location.pathname.includes('/stacks/') ? 'has-audit-nav' : ''}`}>
      <aside className={`sidebar ${mobileOpen ? 'is-open' : ''}`}>
        <div className="brand-row">
          <NavLink to="/app" className="brand" onClick={() => setMobileOpen(false)}>
            <span className="brand-mark" aria-hidden="true" />
            <span>BIOHACK <b>AI</b></span>
          </NavLink>
          <button className="icon-button sidebar-close" onClick={() => setMobileOpen(false)} aria-label={tr('menuClose')}><X size={18} /></button>
        </div>
        <div className="workspace-label">{tr('workspace')}</div>
        <nav className="side-nav" aria-label={tr('workspace')}>
          {nav.map(({ to, key, end }, index) => (
            <NavLink key={to} to={to} end={end} onClick={() => setMobileOpen(false)}>
              <span className="nav-index">{String(index + 1).padStart(2, '0')}</span>
              <span>{tr(key)}</span>
              {key === 'builder' && builderCount ? <em>{builderCount}</em> : null}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-foot">
          <div className="demo-note"><span className={meta ? 'live-dot' : 'live-dot is-off'} /> {meta ? `${tr('databaseReady')} · v${meta.version}` : tr('databaseUnavailable')}</div>
          {meta ? <div className="sidebar-counts"><span>{formatCount(meta.counts.substances, 'substance', language)}</span><span>{formatCount(meta.counts.interactions, 'pair', language)}</span><span>{formatCount(meta.counts.stacks, 'stack', language)}</span></div> : null}
        </div>
      </aside>

      <div className="app-column">
        <header className="utility-bar">
          <div className="utility-context">
            <span>{String(activeSectionIndex + 1).padStart(2, '0')}</span>
            <strong>{tr(activeSection.key)}</strong>
          </div>
          <LanguageSwitch />
        </header>
        <header className="mobile-header">
          <button className="icon-button" onClick={() => setMobileOpen(true)} aria-label={tr('menuOpen')}><Menu size={19} /></button>
          <span className="mobile-brand">BIOHACK AI</span>
          <LanguageSwitch />
        </header>
        <main className="page-stage"><Outlet /></main>
        <footer className="safety-footer">{tr('noMedical')}</footer>
      </div>
      {mobileOpen ? <button className="sidebar-scrim" aria-label={tr('menuClose')} onClick={() => setMobileOpen(false)} /> : null}

      <nav className="mobile-bottom-nav" aria-label={tr('workspace')}>
        {nav.slice(0, 5).map(({ to, key, end }, index) => (
          <NavLink key={to} to={to} end={end}>
            <span className="nav-index mobile-nav-index">{String(index + 1).padStart(2, '0')}</span>
            <span>{mobileLabel(key, language)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  )
}

function LanguageSwitch() {
  const { language, setLanguage } = useLanguage()
  return (
    <div className="language-switch" role="group" aria-label={language === 'ru' ? 'Язык' : 'Language'}>
      {(['ru', 'en'] as Language[]).map((item) => (
        <button key={item} type="button" className={language === item ? 'active' : ''} onClick={() => setLanguage(item)} aria-label={item === 'ru' ? 'Русский' : 'English'} aria-pressed={language === item}>
          {item.toUpperCase()}
        </button>
      ))}
    </div>
  )
}
